/**
 * Project photos (MB decisions 427-431; docs/specs/cloud/project-photos.md).
 *
 * - 427: field crew add photos in Teslyn Field (offline, uploaded when online); staff add them in Teslyn Cloud.
 * - 428: a photo is private to staff until a manager or office staff ticks "Share with client"; clients see shared
 *   photos only (enforced by RLS and the storage policies of `20261021040000_project_photos.sql`; this module only
 *   decides what the apps show).
 * - 429: a photo carries a caption, the date and place taken (from its EXIF when present), who added it and a
 *   category; it is shown in a gallery and as a pin on the project's maps.
 * - 430: photos stay out of the report PDFs (nothing here feeds a report).
 * - 431: photos are stored in Teslyn Cloud (bucket `project-photos`) and copied to the tenant's file archive.
 *
 * Every photo has three files: the original as it was added, a display size (2048 px on the long edge) and a
 * thumbnail (400 px), both JPEG. The two sizes are drawn again from the pixels, so they carry no EXIF: clients are
 * only ever given those two (the storage policy), never the original.
 */
import { importedImageExt, importedImageName, isBitmapExtension } from './image-import.js';
import type { LayerResult, MapFeature } from './map-layers.js';
import type { PhotoShareState, ProjectPhoto } from './model.js';

export type { PhotoShareState, ProjectPhoto } from './model.js';

export type PhotoCategory = 'aircraft' | 'site' | 'base_camp' | 'safety' | 'equipment' | 'other';

/** Decision 429's categories, in the order the pickers list them. */
export const PHOTO_CATEGORIES: ReadonlyArray<{ readonly id: PhotoCategory; readonly label: string }> = [
  { id: 'aircraft', label: 'Aircraft' },
  { id: 'site', label: 'Site' },
  { id: 'base_camp', label: 'Base/camp' },
  { id: 'safety', label: 'Safety' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'other', label: 'Other' },
];

/** Decision 516: a new photo's category until the person chooses another. */
export const DEFAULT_PHOTO_CATEGORY: PhotoCategory = 'site';

export function isPhotoCategory(v: string): v is PhotoCategory {
  return PHOTO_CATEGORIES.some((c) => c.id === v);
}

export function photoCategoryLabel(c: string): string {
  return PHOTO_CATEGORIES.find((x) => x.id === c)?.label ?? 'Other';
}

const MB = 1024 * 1024;

/** Teslyn Cloud on the free plan takes files up to 50 MB (the bucket enforces the same). */
export const PHOTO_MAX_BYTES = 50 * MB;

/** The photo types Teslyn takes, and the content type each is stored with. */
export const PHOTO_TYPES: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

/** What the screens say is accepted (a bitmap is kept as a PNG, R144: `image-import.ts`). */
export const PHOTO_ACCEPTED_TEXT = 'JPEG, PNG, WebP or BMP photos up to 50 MB';

/**
 * HEIC (the iPhone default) is refused: no browser but Safari can show it, and neither Teslyn Field nor Teslyn
 * Cloud can turn it into a JPEG without a licensed decoder (spec §5).
 */
const HEIC = new Set(['heic', 'heif', 'hif']);

export type PhotoCheck =
  | {
      readonly ok: true;
      /** The extension the photo is kept under (a bitmap's is `png`). */
      readonly ext: string;
      readonly contentType: string;
      /** Set for a bitmap (R144): what it was, and the name it is kept under ("strip.bmp" → "strip.png"). */
      readonly converted?: { readonly from: string; readonly fileName: string };
    }
  | {
      readonly ok: false;
      readonly code: 'heic' | 'bad_type' | 'too_large' | 'empty';
      readonly message: string;
    };

function extensionOf(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? name;
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

const mbText = (bytes: number): string => `${(bytes / MB).toFixed(1)} MB`;

/** Whether a file named `fileName` of `sizeBytes` may be added as a project photo. */
export function photoFileCheck(fileName: string, sizeBytes: number): PhotoCheck {
  const name = fileName.split(/[\\/]/).pop() ?? fileName;
  const given = extensionOf(name);
  // a bitmap is kept as a PNG (R144): checked and named as one
  const ext = importedImageExt(given);
  if (HEIC.has(ext)) {
    return {
      ok: false,
      code: 'heic',
      message:
        `${name} is a HEIC photo, which Teslyn cannot show yet. Export it as JPEG first, or set the iPhone to ` +
        'take JPEG photos (Settings, Camera, Formats, Most Compatible).',
    };
  }
  const contentType = PHOTO_TYPES[ext];
  if (!contentType) {
    return {
      ok: false,
      code: 'bad_type',
      message: `${name} is not a photo Teslyn takes. Add JPEG, PNG, WebP or BMP photos.`,
    };
  }
  if (sizeBytes <= 0) return { ok: false, code: 'empty', message: `${name} is empty.` };
  if (sizeBytes > PHOTO_MAX_BYTES) {
    return {
      ok: false,
      code: 'too_large',
      message: `${name} is ${mbText(sizeBytes)}. Teslyn Cloud takes photos up to ${PHOTO_MAX_BYTES / MB} MB: make it smaller and add it again.`,
    };
  }
  if (isBitmapExtension(given)) {
    return { ok: true, ext, contentType, converted: { from: given, fileName: importedImageName(name) } };
  }
  return { ok: true, ext, contentType };
}

export type PhotoRendition = 'original' | 'display' | 'thumb';

/** The two sizes drawn from each photo (long edge in pixels, JPEG quality 0 to 1). */
export const PHOTO_RENDITIONS = {
  display: { maxPx: 2048, quality: 0.85 },
  thumb: { maxPx: 400, quality: 0.8 },
} as const;

/** `width` x `height` scaled to fit `maxPx` on the long edge, never enlarged. */
export function fitWithin(width: number, height: number, maxPx: number): { width: number; height: number } {
  const long = Math.max(width, height);
  if (long <= maxPx) return { width, height };
  const k = maxPx / long;
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

function uuid(v: string): string {
  if (!UUID.test(v)) throw new Error(`${v} is not a valid id.`);
  return v;
}

export const PHOTO_BUCKET = 'project-photos';

/**
 * `tenants/<tenant>/projects/<project>/photos/<photo>/original.<ext>`, `.../display.jpg` or `.../thumb.jpg`: one
 * name per photo and file, so the storage policies can tell from the name which photo an object belongs to.
 */
export function photoObjectPath(
  tenantId: string,
  projectId: string,
  photoId: string,
  rendition: PhotoRendition,
  ext = 'jpg',
): string {
  const base = `tenants/${uuid(tenantId)}/projects/${uuid(projectId)}/photos/${uuid(photoId)}`;
  if (rendition !== 'original') return `${base}/${rendition}.jpg`;
  if (!(ext in PHOTO_TYPES)) throw new Error(`.${ext} is not a photo type Teslyn takes.`);
  return `${base}/original.${ext}`;
}

/** Where Teslyn Field keeps a photo's files, relative to the project folder. */
export const PHOTO_DIR = 'Teslyn/photos';

export function photoLocalPath(photoId: string, rendition: PhotoRendition, ext = 'jpg'): string {
  return rendition === 'original'
    ? `${PHOTO_DIR}/${photoId}.${ext}`
    : `${PHOTO_DIR}/${photoId}.${rendition}.jpg`;
}

// ---------------------------------------------------------------------------------------------------- EXIF

/** What a photo's EXIF says (null where it says nothing usable). */
export interface PhotoExif {
  /** DateTimeOriginal on the camera's clock, `yyyy-mm-ddThh:mm:ss` (no time zone). */
  readonly takenLocal: string | null;
  /** OffsetTimeOriginal, `+hh:mm`, when the camera recorded it. */
  readonly takenOffset: string | null;
  /** WGS 84 degrees from the GPS block, south and west negative. */
  readonly lat: number | null;
  readonly lon: number | null;
  readonly orientation: number | null;
  readonly make: string | null;
  readonly model: string | null;
}

const NO_EXIF: PhotoExif = {
  takenLocal: null,
  takenOffset: null,
  lat: null,
  lon: null,
  orientation: null,
  make: null,
  model: null,
};

/** Why a photo has no place, for the screens (MB request R33: "Photo GPS is not read"). */
export const NO_PLACE_WHY =
  'No place in the photo: the phone\u2019s location was off when it was taken, or the photo came through a messaging ' +
  'app, which removes it.';

/**
 * Whether `head` (the first bytes of the file) ends before its EXIF does: a PNG or WebP keeps the EXIF chunk after
 * the image data, so a larger photo needs more of the file read (R33). JPEGs keep it in the first 64 kB.
 */
export function exifBeyond(head: Uint8Array): boolean {
  return tiffBlock(head) === 'truncated';
}

/** The TIFF block of a JPEG (APP1 "Exif"), PNG (eXIf chunk) or WebP (EXIF chunk); `truncated` when the bytes end
 * before the chunk does; null when the file has none. */
function tiffBlock(b: Uint8Array): Uint8Array | 'truncated' | null {
  const ascii = (o: number, n: number): string =>
    o + n <= b.length ? String.fromCharCode(...b.subarray(o, o + n)) : '';
  const stripExifHeader = (s: Uint8Array): Uint8Array =>
    s.length >= 6 && String.fromCharCode(...s.subarray(0, 6)) === 'Exif\0\0' ? s.subarray(6) : s;
  if (b.length >= 4 && b[0] === 0xff && b[1] === 0xd8) {
    let o = 2;
    while (o + 4 <= b.length) {
      if (b[o] !== 0xff) return null;
      const marker = b[o + 1]!;
      if (marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) {
        o += 2;
        continue;
      }
      if (marker === 0xda || marker === 0xd9) return null; // image data starts: no EXIF before it
      const len = (b[o + 2]! << 8) | b[o + 3]!;
      if (len < 2) return null;
      if (o + 2 + len > b.length) return 'truncated';
      if (marker === 0xe1 && ascii(o + 4, 6) === 'Exif\0\0') return b.subarray(o + 10, o + 2 + len);
      o += 2 + len;
    }
    return null;
  }
  if (ascii(0, 8) === '\x89PNG\r\n\x1a\n') {
    let o = 8;
    while (o + 12 <= b.length) {
      const len = ((b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!) >>> 0;
      const type = ascii(o + 4, 4);
      if (o + 12 + len > b.length) return type === 'IEND' ? null : 'truncated';
      if (type === 'eXIf') return stripExifHeader(b.subarray(o + 8, o + 8 + len));
      if (type === 'IEND') return null;
      o += 12 + len;
    }
    return null;
  }
  if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') {
    let o = 12;
    while (o + 8 <= b.length) {
      const len = (b[o + 4]! | (b[o + 5]! << 8) | (b[o + 6]! << 16) | (b[o + 7]! << 24)) >>> 0;
      if (o + 8 + len > b.length) return 'truncated';
      if (ascii(o, 4) === 'EXIF') return stripExifHeader(b.subarray(o + 8, o + 8 + len));
      o += 8 + len + (len % 2);
    }
    // the chunks ended within the head: the rest of the file (if any) may still hold the EXIF chunk
    return b.length >= 12 && o >= b.length ? 'truncated' : null;
  }
  return null;
}

interface Entry {
  readonly type: number;
  readonly count: number;
  /** Offset of the value (inline or pointed to) within the TIFF block. */
  readonly at: number;
}

/** TIFF field types, bytes each: BYTE, ASCII, SHORT, LONG, RATIONAL, UNDEFINED, SLONG, SRATIONAL, IFD (TIFF 6 §8). */
const TYPE_SIZE: Readonly<Record<number, number>> = {
  1: 1,
  2: 1,
  3: 2,
  4: 4,
  5: 8,
  7: 1,
  9: 4,
  10: 8,
  13: 4,
};

/** Read the EXIF a photo carries: date taken, time zone, GPS place, orientation, camera. Never throws. */
export function readPhotoExif(bytes: Uint8Array): PhotoExif {
  try {
    const t = tiffBlock(bytes);
    if (!t || t === 'truncated' || t.length < 8) return NO_EXIF;
    const le = t[0] === 0x49 && t[1] === 0x49;
    if (!le && !(t[0] === 0x4d && t[1] === 0x4d)) return NO_EXIF;
    const u16 = (o: number): number => {
      if (o < 0 || o + 2 > t.length) throw new RangeError('outside');
      return le ? t[o]! | (t[o + 1]! << 8) : (t[o]! << 8) | t[o + 1]!;
    };
    const u32 = (o: number): number => {
      if (o < 0 || o + 4 > t.length) throw new RangeError('outside');
      return (
        (le
          ? t[o]! | (t[o + 1]! << 8) | (t[o + 2]! << 16) | (t[o + 3]! << 24)
          : (t[o]! << 24) | (t[o + 1]! << 16) | (t[o + 2]! << 8) | t[o + 3]!) >>> 0
      );
    };
    if (u16(2) !== 42) return NO_EXIF;
    const ifd = (off: number): Map<number, Entry> => {
      const out = new Map<number, Entry>();
      if (!off || off + 2 > t.length) return out;
      const n = u16(off);
      for (let k = 0; k < n; k++) {
        const e = off + 2 + k * 12;
        if (e + 12 > t.length) break;
        const type = u16(e + 2);
        const count = u32(e + 4);
        const size = (TYPE_SIZE[type] ?? 0) * count;
        if (size === 0 || count > 1_000_000) continue;
        out.set(u16(e), { type, count, at: size <= 4 ? e + 8 : u32(e + 8) });
      }
      return out;
    };
    const text = (e: Entry | undefined): string | null => {
      if (!e || e.type !== 2 || e.at + e.count > t.length) return null;
      let s = '';
      for (let i = 0; i < e.count; i++) {
        const c = t[e.at + i]!;
        if (c === 0) break;
        s += String.fromCharCode(c);
      }
      s = s.trim();
      return s === '' ? null : s;
    };
    const short = (e: Entry | undefined): number | null => (e && e.type === 3 ? u16(e.at) : null);
    // a pointer to another IFD: LONG, or the IFD type (13) some writers use, or (wrongly) SHORT
    const long = (e: Entry | undefined): number | null =>
      e ? (e.type === 4 || e.type === 13 ? u32(e.at) : e.type === 3 ? u16(e.at) : null) : null;
    // RATIONAL, or SRATIONAL from writers that store signed degrees (the sign is in the N/S, E/W reference)
    const rationals = (e: Entry | undefined, n: number): number[] | null => {
      if (!e || (e.type !== 5 && e.type !== 10) || e.count < n) return null;
      const out: number[] = [];
      for (let i = 0; i < n; i++) {
        const num = e.type === 10 ? u32(e.at + i * 8) | 0 : u32(e.at + i * 8);
        const den = e.type === 10 ? u32(e.at + i * 8 + 4) | 0 : u32(e.at + i * 8 + 4);
        if (den === 0) return null; // an empty GPS block (the phone's location was off)
        out.push(Math.abs(num / den));
      }
      return out;
    };

    const ifd0 = ifd(u32(4));
    const exif = ifd(long(ifd0.get(0x8769)) ?? 0);
    const gps = ifd(long(ifd0.get(0x8825)) ?? 0);

    const dto = text(exif.get(0x9003));
    const m = dto?.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
    const takenLocal =
      m && m[1] !== '0000' && Number(m[2]) >= 1 && Number(m[2]) <= 12 && Number(m[3]) >= 1
        ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`
        : null;
    const offset = text(exif.get(0x9011));
    const takenOffset = takenLocal && offset && /^[+-]\d{2}:\d{2}$/.test(offset) ? offset : null;

    const degrees = (ref: string | null, v: number[] | null, max: number, neg: string, pos: string) => {
      if (!v || (ref !== neg && ref !== pos)) return null;
      const d = v[0]! + v[1]! / 60 + v[2]! / 3600;
      if (!Number.isFinite(d) || d > max) return null;
      return ref === neg ? -d : d;
    };
    let lat = degrees(text(gps.get(0x0001)), rationals(gps.get(0x0002), 3), 90, 'S', 'N');
    let lon = degrees(text(gps.get(0x0003)), rationals(gps.get(0x0004), 3), 180, 'W', 'E');
    if (lat === null || lon === null) {
      lat = null;
      lon = null;
    }
    const orientation = short(ifd0.get(0x0112));
    return {
      takenLocal,
      takenOffset,
      lat,
      lon,
      orientation: orientation !== null && orientation >= 1 && orientation <= 8 ? orientation : null,
      make: text(ifd0.get(0x010f)),
      model: text(ifd0.get(0x0110)),
    };
  } catch {
    return NO_EXIF;
  }
}

// ---------------------------------------------------------------------------------------------------- display

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "26 Jun 2026, 07:20 (UTC+10:00)": the camera's clock as recorded, never converted. */
export function photoTakenText(p: Pick<ProjectPhoto, 'taken_local' | 'taken_offset'>): string {
  const m = p.taken_local?.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return 'Date taken not in the photo';
  const when = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}, ${m[4]}:${m[5]}`;
  return p.taken_offset ? `${when} (UTC${p.taken_offset})` : when;
}

/** "25.09640° S, 117.59030° E", or null when the photo records no place. */
export function photoPlaceText(p: Pick<ProjectPhoto, 'taken_lat' | 'taken_lon'>): string | null {
  if (p.taken_lat === null || p.taken_lon === null) return null;
  const ns = p.taken_lat < 0 ? 'S' : 'N';
  const ew = p.taken_lon < 0 ? 'W' : 'E';
  return `${Math.abs(p.taken_lat).toFixed(5)}° ${ns}, ${Math.abs(p.taken_lon).toFixed(5)}° ${ew}`;
}

/** "935 kB", "2.1 MB". */
export function photoSizeText(bytes: number): string {
  return bytes >= MB ? `${(bytes / MB).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

/** The text a gallery tile and a pin are named by: the caption, else the file name. */
export function photoTitle(p: Pick<ProjectPhoto, 'caption' | 'file_name'>): string {
  return p.caption.trim() || p.file_name;
}

/**
 * Newest first: by the date taken (the camera's clock) where known, else by the date added. The two are compared as
 * text of the same shape, which is good enough for a gallery order.
 */
export function sortPhotos<T extends Pick<ProjectPhoto, 'id' | 'taken_local' | 'created_at'>>(
  photos: readonly T[],
): T[] {
  const key = (p: T): string => p.taken_local ?? p.created_at.slice(0, 19);
  return [...photos].sort((a, b) => key(b).localeCompare(key(a)) || a.id.localeCompare(b.id));
}

/** Photos of the gallery: live ones, newest first. */
export function livePhotos<T extends ProjectPhoto>(photos: readonly T[]): T[] {
  return sortPhotos(photos.filter((p) => p.deleted_at === null));
}

/** A photo as Teslyn Cloud holds it: the cloud-only sharing columns. */
export type CloudPhoto = ProjectPhoto & {
  readonly shared_at?: string | null;
  readonly shared_by?: string | null;
};

/** What a client sees (decision 428): shared, live photos. The database decides the same for client users. */
export function clientVisiblePhotos<T extends CloudPhoto>(photos: readonly T[]): T[] {
  return livePhotos(photos.filter((p) => Boolean(p.shared_at)));
}

/** A pin per photo whose EXIF gave a place (decision 429); the others are listed as not drawn. */
export function photoFeatures(photos: readonly ProjectPhoto[]): LayerResult {
  const features: MapFeature[] = [];
  const notDrawn: string[] = [];
  for (const p of livePhotos(photos)) {
    const title = photoTitle(p);
    if (p.taken_lat === null || p.taken_lon === null) {
      notDrawn.push(`Photo ${title}: no place recorded in the photo`);
      continue;
    }
    const at = { lat: p.taken_lat, lng: p.taken_lon };
    features.push({
      id: p.id,
      layer: 'photos',
      geometry: { type: 'point', at },
      style: { colour: 'accent', stroke: 'normal', dashed: false, fill: 0.8 },
      tooltip: `Photo · ${photoCategoryLabel(p.category)} · ${title}`,
      identify: [
        { label: 'Photo', value: title },
        { label: 'Category', value: photoCategoryLabel(p.category) },
        { label: 'Taken', value: photoTakenText(p) },
        { label: 'Place', value: photoPlaceText(p) ?? '' },
      ],
    });
  }
  return { features, notDrawn };
}

// ---------------------------------------------------------------------------------------------------- roles

/** Every staff role adds photos (decision 427: crews in Field, staff in Teslyn Cloud). */
export const PHOTO_ADD_ROLES = [
  'tenant_admin',
  'manager',
  'field_crew',
  'office_staff',
  'data_processor',
] as const;

/**
 * Decision 515: managers, tenant admins and office staff approve a photo's sharing with the client (and stop it), and
 * they are the ones who delete photos. RLS and the guard of `20261025000700_photo_sharing.sql` enforce the same.
 */
export const PHOTO_SHARE_ROLES = ['manager', 'tenant_admin', 'office_staff'] as const;

/** Decision 515: the roles that delete a photo (the same as those that approve sharing). */
export const PHOTO_DELETE_ROLES = PHOTO_SHARE_ROLES;

export function canAddPhotos(roles: readonly string[]): boolean {
  return roles.some((r) => (PHOTO_ADD_ROLES as readonly string[]).includes(r));
}

/** Approve sharing with the client, or stop it (decision 515). */
export function canSharePhoto(roles: readonly string[]): boolean {
  return roles.some((r) => (PHOTO_SHARE_ROLES as readonly string[]).includes(r));
}

/** Decision 515: any staff member puts a photo forward for sharing. */
export function canPutPhotoForward(roles: readonly string[]): boolean {
  return canAddPhotos(roles);
}

/** Decision 515: managers, tenant admins and office staff delete photos. */
export function canDeletePhoto(roles: readonly string[]): boolean {
  return roles.some((r) => (PHOTO_DELETE_ROLES as readonly string[]).includes(r));
}

/** What Field and Teslyn Cloud show for each state (decision 509). */
export const PHOTO_SHARE_WORDS: Readonly<Record<PhotoShareState, string>> = {
  private: 'Private to staff',
  put_forward: 'Put forward for sharing',
  shared: 'Shared with client',
};

/**
 * A photo's sharing state: shared once a manager, tenant admin or office staff has shared it (`shared_at`, Teslyn
 * Cloud), put forward once a staff member asked for it (`share_requested_at`), else private. A laptop has no
 * `shared_at`: it reads the state Teslyn Cloud keeps (`share_state`).
 */
export function photoShareState(
  p: Pick<ProjectPhoto, 'share_requested_at' | 'share_state'> & { readonly shared_at?: string | null },
): PhotoShareState {
  if (p.shared_at) return 'shared';
  if (p.shared_at === undefined && p.share_state === 'shared') return 'shared';
  if (p.share_requested_at) return 'put_forward';
  return p.share_state === 'put_forward' ? 'put_forward' : 'private';
}

/**
 * Why "Share with client" is greyed out, or null (decision 516): the photo's display size and thumbnail must be in
 * Teslyn Cloud first (the database refuses the share before), and sharing is done online.
 */
export function photoShareBlocker(s: {
  readonly uploaded: boolean;
  readonly online: boolean;
}): string | null {
  if (!s.uploaded) return 'Uploading to Teslyn Cloud: share it once it has uploaded.';
  if (!s.online) return 'Connect to the internet to share it.';
  return null;
}
